import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import { registerAllModules } from 'handsontable/registry';
import { message, Spin, Button, Space, Tooltip, Modal, List, Switch, Typography } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, ReloadOutlined, InfoCircleOutlined, MenuOutlined } from '@ant-design/icons';
import { monthlyMetricService } from '../../services/api';
import 'handsontable/dist/handsontable.full.css';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import './styles.css';

registerAllModules();

const MonthlyMetricsTable = forwardRef((props, ref) => {
  const { brandId, selectedMonth, onMonthlyMetricUpdate } = props;
  const [loading, setLoading] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [metricVisibility, setMetricVisibility] = useState({});
  const [visibilityModalVisible, setVisibilityModalVisible] = useState(false);
  const [sortableMetrics, setSortableMetrics] = useState([]);
  const [sortModalVisible, setSortModalVisible] = useState(false);
  const [draggableEnabled, setDraggableEnabled] = useState(false);
  const [localValues, setLocalValues] = useState({});


  const fetchMonthlyValues = useCallback(async () => {
    if (!brandId || !selectedMonth) return;

    try {
      setLoading(true);
      const values = await monthlyMetricService.getMonthlyMetricValues(
        brandId,
        selectedMonth.format('YYYY-MM')
      );

      // Veriyi sütun formatına dönüştür
      const transformedData = [{
        metric: 'Metrik Adı',
        ...values.reduce((acc, curr) => {
          // isVisible false olanlar hariç sütun oluştur
          if (curr.isVisible !== false) {
            // Name yerine description kullanıyoruz ve 3 kelimeden sonra satır sonu ekliyoruz
            const description = curr.description || curr.name || '';
            const words = description.split(' ');
            let formattedDesc = '';
            
            for (let i = 0; i < words.length; i++) {
              formattedDesc += words[i];
              if ((i + 1) % 3 === 0 && i !== words.length - 1) {
                formattedDesc += '\n';
              } else if (i !== words.length - 1) {
                formattedDesc += ' ';
              }
            }
            
            acc[`col_${curr.id}`] = formattedDesc;
          }
          return acc;
        }, {})
      }, {
        metric: 'Değer',
        ...values.reduce((acc, curr) => {
          // isVisible false olanlar hariç değer ata
          if (curr.isVisible !== false) {
          acc[`col_${curr.id}`] = curr.value;
          }
          return acc;
        }, {})
      }];

      // isAutoCalculated bilgisini sakla
      const autoCalculatedColumns = values.reduce((acc, curr) => {
        if (curr.isVisible !== false) {
        acc[`col_${curr.id}`] = curr.isAutoCalculated;
        }
        return acc;
      }, {});

      // isVisible bilgisini sakla
      const visibilityInfo = values.reduce((acc, curr) => {
        acc[curr.id] = curr.isVisible !== false; // undefined ise true kabul et
        return acc;
      }, {});

      setGridData({
        data: transformedData,
        autoCalculated: autoCalculatedColumns,
        originalData: values
      });
      
      setMetricVisibility(visibilityInfo);
      
      // Sıralanabilir metrikler listesini güncelle
      // Tanımsız değerleri filtrele
      const filteredMetrics = values.filter(metric => metric && metric.id !== undefined);
      
      setSortableMetrics(filteredMetrics.map((metric, index) => {
        const item = {
          id: String(metric.id), // Daima string olarak sakla
          name: metric.name || 'İsimsiz Metrik',
          displayOrder: metric.displayOrder !== undefined ? metric.displayOrder : index,
          isAutoCalculated: !!metric.isAutoCalculated,
          isVisible: metric.isVisible !== false,
          internalNotes: metric.internalNotes
        };
        console.log('Metrik hazırlanıyor:', item);
        return item;
      }));
      
      console.log('Dönüştürülmüş veriler:', transformedData);
      console.log('Metrik görünürlük:', visibilityInfo);
      return values;
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      message.error('Aylık metrik değerleri yüklenirken bir hata oluştu');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [brandId, selectedMonth]);

  useImperativeHandle(ref, () => ({
    fetchMonthlyValues,
    setVisibilityModalVisible: (visible) => setVisibilityModalVisible(visible),
    get gridData() {
      return gridData;
    }
  }), [fetchMonthlyValues, gridData]);

  const handleAfterChange = async (changes) => {
    if (!changes) return;

    for (const [row, prop, oldValue, newValue] of changes) {
      if (row === 1 && prop.startsWith('col_')) { // Sadece değer satırındaki değişiklikleri işle
        const metricId = parseInt(prop.replace('col_', ''));
        const isAutoCalculated = gridData.autoCalculated[prop];
        
        if (isAutoCalculated) {
          message.warning('Bu değer otomatik hesaplanmaktadır');
          // Eski değere geri dön
          const newGridData = {
            ...gridData,
            data: [...gridData.data]
          };
          newGridData.data[row][prop] = oldValue;
          setGridData(newGridData);
          continue;
        }

        try {
          // Önce UI'da değeri güncelle
          const newGridData = {
            ...gridData,
            data: [...gridData.data]
          };
          
          // Burada değer boş string veya null olabilir, bunu doğrudan kaydedelim
          newGridData.data[row][prop] = newValue;
          setGridData(newGridData);

          // API'ye kaydederken, boş string veya null değeri doğrudan null olarak gönder
          const valueToSave = (newValue === '' || newValue === null) ? null : newValue;
          
          // Sonra API'ye kaydet
          await monthlyMetricService.saveMonthlyMetricValue({
            brandId,
            monthlyMetricId: metricId,
            date: selectedMonth.format('YYYY-MM'),
            value: valueToSave
          });

          message.success('Değer başarıyla kaydedildi');
          
          // Aylık metrik güncellendiğinde günlük metrikleri yeniden hesapla
          if (onMonthlyMetricUpdate) {
            console.log('Aylık metrik kaydedildi, günlük metrikleri güncelliyorum...');
            try {
              await onMonthlyMetricUpdate();
            } catch (error) {
              console.error('Günlük metrikleri güncelleme hatası:', error);
            }
          }
        } catch (error) {
          console.error('Kaydetme hatası:', error);
          message.error('Değer kaydedilirken bir hata oluştu');
          // Hata durumunda eski değere geri dön
          const newGridData = {
            ...gridData,
            data: [...gridData.data]
          };
          newGridData.data[row][prop] = oldValue;
          setGridData(newGridData);
        }
      }
    }
  };

  // Tekil metrik görünürlüğü değiştirme
  const handleVisibilityChange = async (metricId) => {
    try {
      const newVisibility = !metricVisibility[metricId];
      
      // UI'da hemen değişikliği yansıt
      setMetricVisibility(prev => ({
        ...prev,
        [metricId]: newVisibility
      }));
      
      // API'ye gönder
      const result = await monthlyMetricService.updateMonthlyMetricVisibility({
        brandId,
        monthlyMetricId: metricId,
        isVisible: newVisibility
      });
      
      if (!result.success) {
        // Hata durumunda eski değere geri dön
        setMetricVisibility(prev => ({
          ...prev,
          [metricId]: !newVisibility
        }));
        throw new Error(result.error);
      }
      
      message.success(`Metrik ${newVisibility ? 'görünür' : 'gizli'} olarak ayarlandı`);
      
      // Görünmez metrik durumunda tabloyu hemen güncelle
      if (!newVisibility) {
        // Görünürlük değişikliği kaydedildikten sonra tabloyu güncelle
        setTimeout(() => {
          fetchMonthlyValues().catch(err => {
            console.error('Tablo güncelleme hatası:', err);
          });
        }, 300);
      }
    } catch (error) {
      console.error('Görünürlük değiştirme hatası:', error);
      message.error('Metrik görünürlüğü değiştirilirken bir hata oluştu');
    }
  };

  const columns = useMemo(() => {
    if (!gridData.data || gridData.data.length === 0) return [];

    return [
      {
        data: 'metric',
        type: 'text',
        readOnly: true,
        width: 100
      },
      ...Object.keys(gridData.data[0])
        .filter(key => key.startsWith('col_'))
        .filter(key => {
          // Sadece görünür metriklere ait sütunları dahil et
          if (key.startsWith('col_')) {
            const metricId = parseInt(key.replace('col_', ''));
            return metricVisibility[metricId] !== false;
          }
          return true; // 'metric' sütunu gibi diğerleri
        })
        .map(key => ({
          data: key,
          type: 'numeric',
          numericFormat: {
            pattern: '0,0.00',
            culture: 'tr-TR'
          },
          // Tüm sütunlar için CSS sınıfı ekliyoruz (özellikle ilk satır için)
          className: 'small-font-metrics'
        }))
    ];
  }, [gridData, metricVisibility]);

  // Tüm metriklerin görünürlüğünü toplu güncelleme
  const handleSetAllVisibility = (visibleState) => {
    if (!gridData.originalData || gridData.originalData.length === 0) return;

    const newVisibility = { ...metricVisibility };
    
    gridData.originalData.forEach(metric => {
      newVisibility[metric.id] = visibleState;
    });
    
    setMetricVisibility(newVisibility);
  };

  // Görünürlük modalini kapatırken tüm değişiklikleri kaydet
  const saveMetricVisibility = async () => {
    if (!gridData.originalData || gridData.originalData.length === 0) return;
    
    setLoading(true);
    
    try {
      // Her metrik için görünürlük ayarını güncelle
      const updatePromises = gridData.originalData.map(metric => 
        monthlyMetricService.updateMonthlyMetricVisibility({
          brandId,
          monthlyMetricId: metric.id,
          isVisible: metricVisibility[metric.id]
        })
      );
      
      const results = await Promise.all(updatePromises);
      
      // Hata kontrolü
      const hasError = results.some(result => !result.success);
      
      if (hasError) {
        message.warning('Bazı metrikler güncellenirken hata oluştu.');
      } else {
        message.success('Aylık metrik görünürlük ayarları başarıyla kaydedildi.');
      }
      
      // Tablodaki verileri yeniden yükle - bu işlem görünmez metrikleri otomatik olarak filtreleyecek
      await fetchMonthlyValues();
      
      setVisibilityModalVisible(false);
    } catch (error) {
      console.error('Görünürlük ayarlarını kaydetme hatası:', error);
      message.error('Görünürlük ayarları kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Görünürlük modalini göster
  const renderVisibilityModal = () => (
    <Modal
      title="Aylık Metrik Görünürlük Ayarları"
      open={visibilityModalVisible}
      onOk={saveMetricVisibility}
      onCancel={() => setVisibilityModalVisible(false)}
      width={600}
      confirmLoading={loading}
      okText="Değişiklikleri Kaydet"
      cancelText="İptal"
    >
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button onClick={() => handleSetAllVisibility(true)}>Tümünü Göster</Button>
          <Button onClick={() => handleSetAllVisibility(false)}>Tümünü Gizle</Button>
        </Space>
      </div>
      <List
        bordered
        dataSource={gridData.originalData || []}
        renderItem={(metric, index) => {
          if (!metric || metric.id === undefined) {
            console.error('Geçersiz metrik:', metric);
            return null;
          }
          
          // String olarak metrik ID'sini oluştur ve güvenilir bir draggableId oluştur
          const metricId = String(metric.id);
          const draggableId = `metric-${metricId}`;
          
          // Oluşturulan ID'yi logla
          console.log(`Rendering Draggable ${index}:`, metric.name, 'id:', metricId, 'draggableId:', draggableId);
          
          return (
            <List.Item
              key={metricId}
              actions={[
                <span key="status" style={{ marginRight: 8 }}>
                  {metricVisibility[metric.id] === true ? 
                    <Typography.Text type="success">Görünür</Typography.Text> : 
                    <Typography.Text type="secondary">Gizli</Typography.Text>
                  }
                </span>,
                <Switch
                  key="switch"
                  checked={metricVisibility[metric.id] === true}
                  onChange={(checked) => {
                    setMetricVisibility(prev => ({
                      ...prev,
                      [metric.id]: checked
                    }));
                  }}
                  checkedChildren={<EyeOutlined />}
                  unCheckedChildren={<EyeInvisibleOutlined />}
                />
              ]}
            >
              <List.Item.Meta
                title={metric.name}
                description={
                  <Tooltip title={metric.internalNotes || (metric.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel')}>
                   
                  </Tooltip>
                }
              />
            </List.Item>
          );
        }}
      />
    </Modal>
  );

  const renderMetricVisibilityControls = () => {
    if (!gridData.originalData || gridData.originalData.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Tooltip title="Yenile">
            <Button 
              type="text" 
              icon={<ReloadOutlined />} 
              onClick={fetchMonthlyValues} 
            />
          </Tooltip>
          
          <Tooltip title="Metrik Görünürlüğü">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setVisibilityModalVisible(true)}
            />
          </Tooltip>
          
          <Tooltip title="Metrik Sıralama">
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setSortModalVisible(true)}
            />
          </Tooltip>
          
        </Space>
      </div>
    );
  };

  // Sürükle-bırak işlemi tamamlandığında
  const handleDragEnd = (result) => {
    const { source, destination } = result;
    
    console.log('handleDragEnd çağrıldı:', result);
    console.log('Mevcut sortableMetrics:', sortableMetrics);
    
    // Eğer hedef yoksa (sürükleme iptal edildiyse) veya aynı konuma bırakıldıysa
    if (!destination || 
        (destination.index === source.index && destination.droppableId === source.droppableId)) {
      return;
    }
    
    // Metrik sırasını yeniden oluştur
    const items = Array.from(sortableMetrics);
    const [movedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, movedItem);
    
    // Yeni sıralama düzenini güncelle
    const updatedItems = items.map((item, index) => ({
      ...item,
      displayOrder: index
    }));
    
    setSortableMetrics(updatedItems);
    
    console.log('Güncellenmiş sortableMetrics:', updatedItems);
  };

  // Sıralama değişikliklerini kaydet
  const saveSortOrder = async () => {
    try {
      setLoading(true);
      const updatedMetrics = sortableMetrics.map(metric => ({
        id: parseInt(metric.id), // Sayı olarak gönder
        displayOrder: metric.displayOrder
      }));
      
      const result = await monthlyMetricService.updateMonthlyMetricDisplayOrder(
        brandId,
        updatedMetrics
      );
      
      if (result && result.success) {
        message.success('Metrik sıralaması başarıyla güncellendi');
        
        // Sunucudan gelen güncellenmiş metrikleri kullan
        if (result.metrics && Array.isArray(result.metrics)) {
          setSortableMetrics(result.metrics.map(metric => ({
            ...metric,
            id: String(metric.id) // ID'yi string olarak saklama
          })));
        }
        
        setSortModalVisible(false);
        fetchMonthlyValues();
      } else {
        throw new Error(result?.message || 'Bir hata oluştu');
      }
    } catch (error) {
      console.error('Sıralama düzeni kaydetme hatası:', error);
      message.error('Sıralama düzeni kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // sortModalVisible değiştiğinde draggableEnabled'ı güncelle
  useEffect(() => {
    if (sortModalVisible) {
      const animation = requestAnimationFrame(() => setDraggableEnabled(true));
      return () => {
        cancelAnimationFrame(animation);
        setDraggableEnabled(false);
      };
    }
  }, [sortModalVisible]);

  // Sıralama modalini göster
  const renderSortModal = () => {
    console.log('Render Sort Modal - sortableMetrics:', sortableMetrics);
    
    // Tüm metriklerin ID'lerini kontrol et
    const metricIds = sortableMetrics.map(m => ({id: m.id, idType: typeof m.id}));
    console.log('Metrik ID\'leri ve tipleri:', metricIds);
    
    return (
      <Modal
        title="Aylık Metrik Sıralama Düzeni"
        open={sortModalVisible}
        onOk={saveSortOrder}
        onCancel={() => setSortModalVisible(false)}
        width={600}
        confirmLoading={loading}
        okText="Değişiklikleri Kaydet"
        cancelText="İptal"
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            Metrikleri sürükleyip bırakarak sıralama düzenini değiştirebilirsiniz.
          </Typography.Text>
        </div>
        
        {draggableEnabled && (
          <React.StrictMode>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="metrics" type="METRIC">
                {(provided, snapshot) => (
                  <List
                    bordered
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="metric-list"
                    dataSource={sortableMetrics}
                    renderItem={(metric, index) => {
                      if (!metric || metric.id === undefined) {
                        console.error('Geçersiz metrik:', metric);
                        return null;
                      }
                      
                      return (
                        <Draggable 
                          key={metric.id} 
                          draggableId={String(metric.id)} 
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <List.Item
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={snapshot.isDragging ? 'dragging-row' : ''}
                              style={{
                                ...provided.draggableProps.style,
                                cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                background: snapshot.isDragging ? '#f0f0f0' : 'white'
                              }}
                            >
                              <List.Item.Meta
                                avatar={<MenuOutlined className="drag-visible" />}
                                title={metric.name || 'İsimsiz Metrik'}
                                description={
                                  <Tooltip title={metric.internalNotes || (metric.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel')}>
                                    
                                  </Tooltip>
                                }
                              />
                            </List.Item>
                          )}
                        </Draggable>
                      );
                    }}
                  >
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </React.StrictMode>
        )}
      </Modal>
    );
  };

  useEffect(() => {
    fetchMonthlyValues();
  }, [brandId, selectedMonth, fetchMonthlyValues]);

  if (loading) {
    return <Spin />;
  }

  return (
    <div style={{ marginBottom: 24 }}>
      {renderMetricVisibilityControls()}
      {renderVisibilityModal()}
      {renderSortModal()}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        {gridData.data && gridData.data[0] && Object.entries(gridData.data[0])
          .filter(([key]) => key !== 'metric')
          .map(([key, title], index) => {
            const value = gridData.data[1] ? gridData.data[1][key] : null;
            const isAutoCalculated = gridData.autoCalculated && gridData.autoCalculated[key];
            const metricId = parseInt(key.replace('col_', ''));
            const localValue = localValues[key] !== undefined ? localValues[key] : value;
            
            return (
              <div key={key} style={{ 
                flex: '1 1 calc(12.5% - 16px)',
                minWidth: '100px',
                maxWidth: 'calc(12.5% - 16px)',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                padding: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div className="small-font-metrics" style={{ 
                  fontSize: '14px', 
                  fontWeight: 'bold',
                  color: '#1890ff',
                  marginBottom: '8px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Tooltip title={() => {
                    const metric = gridData.originalData.find(m => m.id === metricId);
                    return metric?.internalNotes || (metric?.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel');
                  }}>
                    <span>{title}</span>
                  </Tooltip>
                  {(
                    <Tooltip title={() => {
                      const metric = gridData.originalData.find(m => m.id === metricId);
                      return metric?.internalNotes || (metric?.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel');
                    }}>
                      <InfoCircleOutlined style={{ color: '#faad14' }} />
                    </Tooltip>
                  )}
                </div>
                {isAutoCalculated ? (
                  <div style={{
                    fontSize: '16px',
                    color: '#595959',
                    backgroundColor: '#fafafa',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}>
                    {value !== null && value !== undefined ? 
                      (
                        // Metrik tipini bulmak için originalData'dan ilgili metriği bul
                        (() => {
                          const metric = gridData.originalData.find(m => m.id === metricId);
                          if (metric && metric.dataType === 'percentage') {
                            // Yüzde tipi için % işareti ekle
                            return parseFloat(value).toLocaleString('tr-TR') + (String(value).includes('%') ? '' : '%');
                          } else {
                            // Diğer sayısal tipler için sadece formatla
                            return parseFloat(value).toLocaleString('tr-TR');
                          }
                        })()
                      ) : '-'}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={localValue !== null && localValue !== undefined ? 
                      // Düzenleme modunda ise ham değeri göster (formatlamadan)
                      localValue : 
                      // Ham değeri göster, formatlanmış değil
                      (value !== null && value !== undefined ? value : '')
                    }
                    onChange={(e) => {
                      // Değeri doğrudan yerel state'e kaydet (formatlamadan)
                      setLocalValues(prev => ({
                        ...prev,
                        [key]: e.target.value
                      }));
                    }}
                    onBlur={(e) => {
                      // Değerden tüm formatlamayı kaldır ve sayısal olarak işle
                      const inputValue = e.target.value;
                      
                      // Boş string veya null kontrolü
                      if (inputValue === '' || inputValue === null) {
                        if (value !== null) {
                          // Değer null olacak şekilde değişikliği kaydet
                          const changes = [[1, key, value, null]];
                          handleAfterChange(changes);
                        }
                      } else {
                        // Sayısal değerler için parse et
                        const cleanValue = inputValue.replace(/[^0-9.-]/g, '');
                        let newValue = cleanValue;
                        
                        try {
                          if (cleanValue !== '') {
                            newValue = parseFloat(cleanValue);
                            if (isNaN(newValue)) newValue = null;
                          }
                        } catch (err) {
                          newValue = null;
                        }
                        
                        // Değer değiştiyse kaydet
                        if (newValue !== value) {
                          const changes = [[1, key, value, newValue]];
                          handleAfterChange(changes);
                        }
                      }
                      
                      // Local value'yu temizle
                      setLocalValues(prev => ({
                        ...prev,
                        [key]: undefined
                      }));
                    }}
                    style={{
                      width: '100%',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#262626',
                      backgroundColor: 'white',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #40a9ff',
                      outline: 'none'
                    }}
                    placeholder="Değer girin"
                  />
                )}
              </div>
            );
        })}
      </div>
    </div>
  );
});

MonthlyMetricsTable.displayName = 'MonthlyMetricsTable';

export default MonthlyMetricsTable;